/**
 * Regras de desconto por quantidade ("leve 2, cada uma sai por menos").
 *
 * A regra é por MODELO de produto (product_id), somando todas as cores e
 * tamanhos do mesmo modelo dentro da mesma venda. O desconto incide sempre
 * sobre o preço base já promocional da peça — nunca sobre o preço cheio.
 */

import { supabase } from "@/integrations/supabase/client";

export type QuantityRuleChannel = "pdv" | "manual" | "site";

export interface QuantityPriceRule {
  id: string;
  name: string;
  min_quantity: number;
  discount_type: "fixed" | "percent";
  discount_value: number;
  /** Quando true, todos os modelos da regra somam quantidade juntos. */
  combine_products: boolean;
  productIds: string[];
  /** "products" = modelos escolhidos; "color_groups" = peças cuja cor está nos grupos. */
  scope: "products" | "color_groups";
  /** Nomes de cor (minúsculos) dos grupos escolhidos — só para scope color_groups. */
  colorNames: string[];
  /** Chaves "productId|cor" das peças nos grupos de cor de Produtos. */
  variantKeys: string[];
}

export const normalizeRuleColor = (c?: string | null) => (c || "").trim().toLowerCase();

export interface QuantityRuleLineInput {
  /** ID do modelo (products.id) */
  productId?: string | number | null;
  /** Preço unitário já considerando promoção de modelo/cor */
  basePrice: number;
  quantity: number;
  /** Nome da cor da peça (para regras por grupo de cor) */
  colorName?: string | null;
}

export interface QuantityRuleLineResult {
  unitPrice: number;
  discountPerUnit: number;
  appliedRuleName?: string;
  appliedRuleId?: string;
}

const round2 = (v: number) => Math.round(v * 100) / 100;

/** Falha ao consultar as regras (diferente de "não há regras"). */
export class QuantityRulesLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "QuantityRulesLoadError";
  }
}

export function mapQuantityRuleRows(data: unknown): QuantityPriceRule[] {
  if (!Array.isArray(data)) throw new QuantityRulesLoadError("resposta inválida");
  return data
    .map((r: any): QuantityPriceRule => ({
      id: String(r.id),
      name: String(r.name ?? ""),
      min_quantity: Number(r.min_quantity) || 2,
      discount_type: r.discount_type === "percent" ? "percent" : "fixed",
      discount_value: Number(r.discount_value) || 0,
      combine_products: !!r.combine_products,
      productIds: (Array.isArray(r.product_ids) ? r.product_ids : []).map((id: any) => String(id)),
      scope: r.scope === "color_groups" ? "color_groups" : "products",
      colorNames: (Array.isArray(r.color_names) ? r.color_names : []).map((c: any) => normalizeRuleColor(String(c))).filter(Boolean),
      variantKeys: (Array.isArray(r.variant_keys) ? r.variant_keys : []).map((k: any) => String(k)),
    }))
    .filter((r) => (r.scope === "color_groups" ? r.colorNames.length + r.variantKeys.length > 0 : r.productIds.length > 0) && r.discount_value > 0);
}

/**
 * Busca as regras ativas e vigentes para o canal informado.
 * A filtragem (ativa, vigência, canal, produtos, desconto > 0) e a autorização
 * dos canais internos são feitas no servidor (get_quantity_price_rules).
 *
 * Sucesso sem regras => []. Falha => lança QuantityRulesLoadError
 * (nunca devolve lista vazia em caso de erro).
 */
export async function fetchQuantityRules(
  channel: QuantityRuleChannel
): Promise<QuantityPriceRule[]> {
  let res: { data: unknown; error: any };
  try {
    res = await (supabase as any).rpc("get_quantity_price_rules", { p_channel: channel });
  } catch (e: any) {
    throw new QuantityRulesLoadError(e?.message || "falha de rede");
  }
  if (res.error) throw new QuantityRulesLoadError(res.error.message || "falha na consulta");
  return mapQuantityRuleRows(res.data);
}

export type QuantityRulesStatus = "loading" | "ok" | "error";

/**
 * Bloqueio de avanço/confirmação: só libera com as regras carregadas com
 * sucesso. Sem itens não há o que bloquear. Deve ser usado ANTES de qualquer
 * cobrança (passagem para pagamento / criação do pedido), nunca na
 * recuperação de venda já aprovada.
 */
export function quantityRulesBlockReason(
  status: QuantityRulesStatus,
  hasItems: boolean
): string | null {
  if (!hasItems || status === "ok") return null;
  if (status === "loading") return "Aguarde: carregando descontos por quantidade.";
  return "Não foi possível carregar os descontos por quantidade. Tente novamente antes de continuar.";
}

function discountFor(rule: QuantityPriceRule, basePrice: number): number {
  const raw =
    rule.discount_type === "percent"
      ? basePrice * (rule.discount_value / 100)
      : rule.discount_value;
  return Math.max(0, Math.min(round2(raw), round2(basePrice)));
}

/**
 * Aplica as regras às linhas da venda. Devolve um resultado por linha,
 * na mesma ordem recebida.
 */
export function applyQuantityRules(
  lines: QuantityRuleLineInput[],
  rules: QuantityPriceRule[]
): QuantityRuleLineResult[] {
  const pidOf = (l: QuantityRuleLineInput) => (l.productId == null ? "" : String(l.productId));
  const lineMatches = (rule: QuantityPriceRule, l: QuantityRuleLineInput) => {
    const pid = pidOf(l);
    if (!pid) return false;
    if (rule.scope === "color_groups") {
      const c = normalizeRuleColor(l.colorName);
      return !!c && (rule.colorNames.includes(c) || rule.variantKeys.includes(`${pid}|${c}`));
    }
    return rule.productIds.includes(pid);
  };

  // Quantidade por regra: combinada (todas as peças elegíveis) ou por modelo.
  const qtyFor = (rule: QuantityPriceRule, pid: string) => {
    let sum = 0;
    for (const l of lines) {
      if (!lineMatches(rule, l)) continue;
      if (!rule.combine_products && pidOf(l) !== pid) continue;
      sum += Number(l.quantity) || 0;
    }
    return sum;
  };

  return lines.map((line) => {
    const pid = pidOf(line);
    const base = round2(Number(line.basePrice) || 0);
    const fallback: QuantityRuleLineResult = { unitPrice: base, discountPerUnit: 0 };
    if (!pid || base <= 0) return fallback;

    let best: { rule: QuantityPriceRule; discount: number } | null = null;
    for (const rule of rules) {
      if (!lineMatches(rule, line)) continue;
      // Grupo de cor: desconto individual por peça, sem quantidade mínima.
      if (rule.scope !== "color_groups" && qtyFor(rule, pid) < rule.min_quantity) continue;
      const discount = discountFor(rule, base);
      if (discount <= 0) continue;
      if (!best || discount > best.discount) best = { rule, discount };
    }
    if (!best) return fallback;

    return {
      unitPrice: round2(Math.max(0, base - best.discount)),
      discountPerUnit: best.discount,
      appliedRuleName: best.rule.name,
      appliedRuleId: best.rule.id,
    };
  });
}
